import { QueryFunctionContext } from '@tanstack/react-query'
import { yyyyMMdd } from '@utils/dates'
import { axiosClient } from 'lib/axios'
import { DateTime } from 'luxon'
import { trim } from '@utils/trim'
import { SavedReport, ReportTypeMap } from '@features/reporting/types'
import {
  savedReportFilterDeserializer,
  SavedReportWithSerializedDateTimes,
} from '@features/reporting/utils/savedReportFilterDeserializer'
import { toDotSnakeCase } from '@utils/toDotSnakeCase'
import { filterToDotSnakeCase } from '@features/reporting/utils/filterToDotSnakeCase'
import { CorrectionAction, CorrectionActionFilter } from 'types'

export const defaultQueryFunction = async <T>({
  queryKey,
}: QueryFunctionContext): Promise<T> => {
  const apiRoute = queryKey.join('')
  return (await axiosClient.get<T>(apiRoute)).data
}

export const searchTimeCards = async (
  userId: number,
  start: DateTime,
  end: DateTime,
): Promise<TimeCard[]> => {
  const response = await axiosClient.get<TimeCard[]>(
    `/users/${userId}/time_card_searches`,
    { params: { startDate: start, endDate: end } },
  )

  return response.data
}

export const searchAdminTimeCards = async (start: DateTime, end: DateTime) => {
  const response = await axiosClient.get<AdminTimeCard[]>(
    '/admin/time_card_searches',
    { params: { startDate: start, endDate: end } },
  )

  return response.data
}

export const updateTimeCard = async (options: {
  userId: number
  id: number
  startAt: number | null
  endAt: number | null
}) => {
  const partialCard = {
    startAt: options.startAt,
    endAt: options.endAt,
  }
  return axiosClient.put(
    `/users/${options.userId}/time_cards/${options.id}`,
    partialCard,
  )
}

export const postBreak = (options: {
  timeCardId: number
  startAt: number | null
  endAt: number | null
  isMeal: boolean
}) => {
  return axiosClient.post(`/time_cards/${options.timeCardId}/breaks`, {
    startAt: options.startAt,
    endAt: options.endAt,
    isMeal: options.isMeal,
  })
}

export const updateBreak = ({
  breakId,
  startAt,
  endAt,
  isMeal,
}: {
  breakId: number
  startAt: number | null
  endAt: number | null
  isMeal: boolean
}) => {
  return axiosClient.put(`/breaks/${breakId}`, {
    startAt,
    endAt,
    isMeal,
  })
}

export const deleteBreak = (breakId: number) => {
  return axiosClient.delete(`/breaks/${breakId}`)
}

export const postTimeCard = (userId: number, date: DateTime) => {
  const card = { date: yyyyMMdd(date) }
  return axiosClient.post(`/users/${userId}/time_cards`, card)
}

export const getEntriesByTimeCardId = async (timeCardId: number) => {
  const response = await axiosClient.get<Entry[]>(
    `/time_cards/${timeCardId}/entries`,
  )

  return response.data
}

export const searchEntries = async (
  userId: number,
  start: DateTime,
  end: DateTime,
) => {
  const response = await axiosClient.get<Entry[]>(
    `/users/${userId}/time_log_entry_searches`,
    { params: { startDate: start, endDate: end } },
  )
  return response.data
}

interface upsertOrDeleteEntryArgs {
  entry: Entry
  stateHandler?: React.Dispatch<React.SetStateAction<number>>
}

const deleteEntry = (entry: Entry) => {
  return axiosClient.delete(`/time_cards/${entry.timeCard.id}/entries/delete`, {
    params: {
      taskId: entry.task.id,
    },
  })
}

const upsertEntry = async (entry: Entry) => {
  return axiosClient.put<Entry>(
    `/time_cards/${entry.timeCard.id}/entries/upsert`,
    {
      duration: entry.duration,
      taskId: entry.task.id,
    },
  )
}

export const upsertOrDeleteEntry = ({ entry }: upsertOrDeleteEntryArgs) => {
  if (entry.duration === 0) {
    return deleteEntry(entry)
  } else {
    return upsertEntry(entry)
  }
}

export const deleteTimeCard = async (options: {
  userId: number
  timeCardId: number
}) => {
  return axiosClient.delete(
    `/users/${options.userId}/time_cards/${options.timeCardId}`,
  )
}

export const submitTimeCard = async (options: {
  userId: number
  timeCardId: number
}) => {
  return axiosClient.post(
    `/users/${options.userId}/time_cards/${options.timeCardId}/submit`,
  )
}

export const unsubmitTimeCard = async (options: {
  userId: number
  timeCardId: number
}) => {
  return axiosClient.post(
    `/users/${options.userId}/time_cards/${options.timeCardId}/unsubmit`,
  )
}

export const getAssignedTasks = async (
  userId: number,
  active: undefined | boolean,
) => {
  const response = await axiosClient.get<Task[]>(
    `/users/${userId}/tasks/assigned`,
    { params: active === undefined ? {} : { active } },
  )
  return response.data
}

export const getSelectedTasks = async (
  userId: number,
  startDate: DateTime,
  endDate: DateTime,
) => {
  const response = await axiosClient.get<SelectedTask[]>(
    `/users/${userId}/task_selection_searches`,
    { params: { startDate, endDate } },
  )
  return response.data
}

export const selectTask = (
  userId: number,
  startDate: DateTime,
  taskId: number,
) => {
  return axiosClient.post(`/users/${userId}/tasks/${taskId}/select`, null, {
    params: { startDate },
  })
}

export const deselectTask = (
  userId: number,
  startDate: DateTime,
  taskId: number,
) => {
  return axiosClient.delete(`/users/${userId}/tasks/${taskId}/deselect`, {
    params: { startDate },
  })
}

export const getNonProjectTasks = async () => {
  const response = await axiosClient.get<NonProjectTask[]>('/non_project_tasks')
  return response.data
}

export const getPinnedTasks = async (userId: number) => {
  const response = await axiosClient.get<Task[]>(
    `/users/${userId}/tasks/pinned`,
  )
  return response.data
}

export const pinTask = async (userId: number, taskId: number) => {
  return axiosClient.post(`/users/${userId}/tasks/${taskId}/pin`)
}

export const unpinTask = async (userId: number, taskId: number) => {
  return axiosClient.delete(`/users/${userId}/tasks/${taskId}/pin`)
}

export const getBannerMessages = async (userId: number) => {
  const response = await axiosClient.get<BannerMessage[]>(
    `/users/${userId}/banner_messages`,
  )
  return response.data
}

export const dismissBannerMessage = async ({
  userId,
  bannerMessageId,
}: {
  userId: number
  bannerMessageId: number
}) => {
  return axiosClient.delete(
    `/users/${userId}/banner_messages/${bannerMessageId}`,
  )
}

const normalizeQueryString = (q: string | undefined) => {
  if (q === undefined) return undefined
  const trimmed = trim(q)
  if (trimmed === '') return undefined

  return trimmed
}

export const multisearch = async ({
  q,
}: MultisearchOptions): Promise<MultisearchResult> => {
  return (
    await axiosClient.get<MultisearchResult>('/tasks/multisearch', {
      params: { q: normalizeQueryString(q) },
    })
  ).data
}

export const multisearchByType = async ({
  q,
  type,
  after,
}: MultisearchByTypeOptions & {
  after?: string
}) => {
  return (
    await axiosClient.get<MultisearchByTypeResult<typeof type>>(
      `/tasks/multisearch/${type}`,
      {
        params: { q: normalizeQueryString(q), after },
      },
    )
  ).data
}

export const searchTasks = async ({
  projectId,
  integrationId,
  after,
  q,
  path,
  active,
}: SearchTaskOptions & { after?: string }) => {
  return (
    await axiosClient.get<Paginated<Task>>(
      `/projects/${projectId}/integrations/${integrationId}/tasks/path_search`,
      { params: { after, query: q, path, active } },
    )
  ).data
}

export const searchTimeLoggerTasks = async ({
  projectId,
  after,
  q,
  active,
}: SearchTimeLoggerTaskOptions & { after?: string }) => {
  return (
    await axiosClient.get<Paginated<Task>>(
      `/projects/${projectId}/time_logger_tasks`,
      { params: { after, query: q, active } },
    )
  ).data
}

export const searchExploreTasks = async ({
  projectId,
  integrationId,
  after,
  q,
  subpath,
  active,
}: SearchExploreTaskOptions & { after?: string }) => {
  return (
    await axiosClient.get<Paginated<{ label: string }>>(
      `/projects/${projectId}/integrations/${integrationId}/tasks/explore`,
      { params: { after, query: q, subpath, active } },
    )
  ).data
}

export const getLinkedProjectIntegrations = async (projectId: number) => {
  return (
    await axiosClient.get<ProjectIntegration[]>(
      `/projects/${projectId}/integrations`,
    )
  ).data
}

export const getMe = async () => {
  return axiosClient.get<Me>('/users/me')
}

export const login = (googleJwt: string) => {
  return axiosClient.post('/login', { token: googleJwt })
}

export const logout = () => {
  return axiosClient.post('/logout')
}

export const searchAbsences = async (
  userId: number,
  startDate: DateTime,
  endDate: DateTime,
  signal?: AbortSignal,
) => {
  const response = await axiosClient.get<Absence[]>(
    `/users/${userId}/absence_searches`,
    { params: { startDate, endDate }, signal },
  )
  return response.data
}

export const refreshAbsences = async ({
  employeeId,
  startDate,
  endDate,
}: {
  employeeId: number
  startDate: DateTime
  endDate: DateTime
}) => {
  return axiosClient.post(`/employees/${employeeId}/refresh_absences`, {
    startDate,
    endDate,
  })
}

export const getSettings = async (userId: number) => {
  const response = await axiosClient.get<Settings>(`/users/${userId}/settings`)
  return response.data
}

export const saveSettings = (userId: number, settings: Settings) => {
  return axiosClient.put(`/users/${userId}/settings`, { settings })
}

export const getProjects = async () => {
  const response = await axiosClient.get<Project[]>('/projects')
  return response.data
}

export const getAdminProjects = async () => {
  const response = await axiosClient.get<AdminProject[]>('/admin/projects')
  return response.data
}

export const createProject = async (name: string) => {
  const response = await axiosClient.post<AdminProject[]>('/admin/projects', {
    name,
  })
  return response.data
}

export const updateProject = async (id: number, name: string) => {
  return axiosClient.put(`/admin/projects/${id}`, {
    name,
  })
}

export const getAdminProjectTasks = async (projectId: number) => {
  const response = await axiosClient.get<AdminProjectTask[]>(
    `/admin/projects/${projectId}/time_logger_tasks`,
  )
  return response.data
}

export const createAdminProjectTask = async (
  projectId: number,
  name: string,
) => {
  const response = await axiosClient.post<AdminProjectTask>(
    `/admin/projects/${projectId}/time_logger_tasks`,
    { task: { name } },
  )
  return response.data
}

export const updateAdminProjectTask = async (
  projectId: number,
  taskId: number,
  name: string,
) => {
  const response = await axiosClient.patch<AdminProjectTask>(
    `/admin/projects/${projectId}/time_logger_tasks/${taskId}`,
    { task: { name } },
  )
  return response.data
}

export const deleteAdminProjectTask = async (
  projectId: number,
  taskId: number,
) => {
  return axiosClient.delete(
    `/admin/projects/${projectId}/time_logger_tasks/${taskId}`,
  )
}

export const refreshWorkdayProjects = async () => {
  return axiosClient.post('/admin/projects/refresh_workday')
}

export const searchHolidays = async (
  holidayCalendarId: string,
  startDate: DateTime,
  endDate: DateTime,
) => {
  const response = await axiosClient.get<Holiday[]>(
    `/holiday_calendars/${holidayCalendarId}/holiday_searches`,
    { params: { startDate, endDate } },
  )
  return response.data
}

export const getEmployee = async (userId: number, date: DateTime) => {
  const employee = await axiosClient.get<GetEmployee>(
    `/users/${userId}/employee`,
    { params: { date } },
  )
  return employee.data
}

export const searchEmployees = async (start: DateTime, end: DateTime) => {
  const employee = await axiosClient.get<TWorker[]>('/employee_searches', {
    params: { startDate: start, endDate: end },
  })
  return employee.data
}

export interface SearchUsersOptions {
  q?: string
  after?: number
}

export const getUsers = async () => {
  const response = await axiosClient.get<User[]>('/users')
  return response.data
}

export const getUser = async (userId: number) => {
  const response = await axiosClient.get<User>(`/users/${userId}`)
  return response.data
}

export const createUser = async ({ email }: { email: string }) => {
  const response = await axiosClient.post<User>('/admin/users', { email })
  return response.data
}

export const getUserAdminRoles = async (userId: number) => {
  const response = await axiosClient.get<UserAdminRoles>(
    `/admin/users/${userId}/admin_roles`,
  )
  return response.data
}

export const getUserSSO = async (userId: number) => {
  const response = await axiosClient.get<UserSSO>(`/admin/users/${userId}/sso`)
  return response.data
}

export const updateUserAdminRoles = async (
  userId: number,
  adminRoles: Partial<UserAdminRoles>,
) => {
  const response = await axiosClient.patch<UserAdminRoles>(
    `/admin/users/${userId}/admin_roles`,
    adminRoles,
  )
  return response.data
}

export const searchApprovals = async (
  start: DateTime,
  end: DateTime,
): Promise<Approval[]> => {
  const response = await axiosClient.get<Approval[]>('/approval_searches', {
    params: { startDate: start, endDate: end },
  })
  return response.data
}

// A 202 means the batch is being processed in the background and returns a
// Batch Operation to poll; a 200 means it was processed within the request,
// with no body, so we return null.
export const approveApprovals = async (approvalIds: number[]) => {
  const response = await axiosClient.post<BatchOperationResponse>(
    '/batch_approvals/approve',
    { approvalIds },
  )

  return response.status === 202 ? response.data : null
}

export const unapproveApprovals = async (approvalIds: number[]) => {
  const response = await axiosClient.post<BatchOperationResponse>(
    '/batch_approvals/unapprove',
    { approvalIds },
  )

  return response.status === 202 ? response.data : null
}

export const getApprovalGroups = async () => {
  const response = await axiosClient.get<ApprovalGroup[]>('/approval_groups')
  return response.data
}

export const getAdminApprovalGroups = async () => {
  const response = await axiosClient.get<AdminApprovalGroup[]>(
    '/admin/approval_groups',
  )
  return response.data
}

export const renameApprovalGroup = async (id: number, name: string) => {
  return axiosClient.put(`/admin/approval_groups/${id}`, {
    name,
  })
}

export const createApprovalGroup = async (name: string) => {
  return axiosClient.post('/admin/approval_groups', { name })
}

// Pass null to clear the default group manager, meaning the group is no longer
// the default destination for any manager's new hires
export const updateApprovalGroupDefaultGroupManager = async (
  id: number,
  directReportsOfWid: number | null,
) => {
  const response = await axiosClient.patch<AdminApprovalGroup>(
    `/admin/approval_groups/${id}`,
    { directReportsOfWid },
  )
  return response.data
}

export const getEmployees = async () => {
  const response = await axiosClient.get<TWorker[]>('/employees')
  return response.data
}

export const updateEmployeeApprovalGroup = async (
  workdayWorkerId: number,
  approvalGroupId: number,
) => {
  return axiosClient.put(`/employees/${workdayWorkerId}/approval_group`, {
    approvalGroupId,
  })
}

export const toggleApprovalGroupAutoApproval = async (id: number) => {
  return axiosClient.post(`/admin/approval_groups/${id}/toggle_auto_approval`)
}

export const toggleProjectAutoApproval = async (id: number) => {
  return axiosClient.post(`/admin/projects/${id}/toggle_auto_approval`)
}

export const updateProjectApprovers = async (
  id: number,
  approverUserIds: number[],
) => {
  return axiosClient.post(`/admin/projects/${id}/update_approvers`, {
    approverUserIds,
  })
}

export const archiveProject = async (id: number) => {
  return axiosClient.post(`/admin/projects/${id}/archive`)
}

export const unarchiveProject = async (id: number) => {
  return axiosClient.post(`/admin/projects/${id}/unarchive`)
}

export const createApprovalDomainApprovers = async (
  approvalDomainGlobalId: string,
  userIds: number[],
  allFacilities: boolean,
  allWorkdayDepartments: boolean,
  facilityIds: number[],
  workdayDepartmentIds: string[],
) => {
  const response = await axiosClient.post<Approver[]>(
    `/admin/approval_domains/${approvalDomainGlobalId}/approvers`,
    {
      userIds,
      allFacilities,
      allWorkdayDepartments,
      facilityIds,
      workdayDepartmentIds,
    },
  )
  return response.data
}

export const getApprovalDomainApprovers = async (
  approvalDomainGlobalId: string,
) => {
  const response = await axiosClient.get<Approver[]>(
    `/admin/approval_domains/${approvalDomainGlobalId}/approvers`,
  )
  return response.data
}

export const deleteApprovalDomainApprover = async (
  approvalDomainGlobalId: string,
  approverId: number,
) => {
  return axiosClient.delete(
    `/admin/approval_domains/${approvalDomainGlobalId}/approvers/${approverId}`,
  )
}

export const updateApprovalDomainApprover = async (
  approvalDomainGlobalId: string,
  approverId: number,
  allFacilities: boolean,
  allWorkdayDepartments: boolean,
  facilityIds: number[],
  workdayDepartmentIds: string[],
) => {
  const response = await axiosClient.put<Approver>(
    `/admin/approval_domains/${approvalDomainGlobalId}/approvers/${approverId}`,
    {
      allFacilities,
      allWorkdayDepartments,
      facilityIds,
      workdayDepartmentIds,
    },
  )
  return response.data
}

export const deleteNote = async (employeeId: number, date: DateTime) => {
  return axiosClient.delete(
    `/employees/${employeeId}/time_logging_notes/${date.toISODate()}`,
  )
}

export const getNotes = async (
  employeeId: number,
  start: DateTime,
  end: DateTime,
) => {
  const response = await axiosClient.get<{ date: DateTime; body: string }[]>(
    `/employees/${employeeId}/time_logging_notes_searches`,
    /* eslint-disable camelcase */
    { params: { start_date: start.toISODate(), end_date: end.toISODate() } },
    /* eslint-enable camelcase */
  )

  return response.data
}

export const putNote = async (
  employeeId: number,
  date: DateTime,
  note: string,
) => {
  return axiosClient.put(
    `/employees/${employeeId}/time_logging_notes/${date.toISODate()}`,
    { body: note },
  )
}

export const getApprovalTaggedTime = async (approvalId: number) => {
  const response = await axiosClient.get<TaggedTime[]>(
    `/approvals/${approvalId}/tagged_time`,
  )
  return response.data
}

export const getTimeCardTaggedTime = async (timeCardId: number) => {
  const response = await axiosClient.get<TaggedTime[]>(
    `/time_cards/${timeCardId}/tagged_time`,
  )
  return response.data
}

export const getWorkdayTimeBlockErrors = async (timeCardId: number) => {
  const response = await axiosClient.get<WorkdayTimeBlockError[]>(
    `/admin/time_cards/${timeCardId}/workday_time_block_errors`,
  )
  return response.data
}

export const getEmployeeDayEventLog = async (
  employeeId: number,
  date: DateTime,
) => {
  const response = await axiosClient.get<EmployeeDayEventLogEntry[]>(
    `/employees/${employeeId}/day_event_log?date=${date.toISODate()}`,
  )
  return response.data
}

export const getApprovalEventLog = async (approvalId: number) => {
  const response = await axiosClient.get<ApprovalEventLogEntry[]>(
    `/approvals/${approvalId}/event_log`,
  )
  return response.data
}

export type DateFilter<RT extends keyof ReportTypeMap> = {
  column: ReportTypeMap[RT]['pageColumn']
  type: 'date'
} & (
  | {
      relativeDateRange: string // snake_case of RelativeDateRangeValue
    }
  | {
      startDate: DateTime
      endDate: DateTime
    }
)

/* commenting out until it's used
interface DurationRangeFilter {
  column: string
  type: 'duration_range'
  from?: number // seconds
  to?: number // seconds
}
*/

export type SearchFilter<RT extends keyof ReportTypeMap> = {
  column: ReportTypeMap[RT]['pageColumn']
  type: 'search_query'
  query: string
}

export type ValueFilter<RT extends keyof ReportTypeMap> = {
  column: ReportTypeMap[RT]['pageColumn']
  type: 'value'
  selected: (number | string)[]
}

export type NumericRangeFilter<RT extends keyof ReportTypeMap> = {
  column: ReportTypeMap[RT]['pageColumn']
  type: 'numeric_range'
  from?: number
  to?: number
}

export type ReportFilter<RT extends keyof ReportTypeMap> = (
  | DateFilter<RT>
  | SearchFilter<RT>
  | ValueFilter<RT>
  | NumericRangeFilter<RT>
)[]

export type GetReportPageOptions<RT extends keyof ReportTypeMap> = {
  page?: number
  pageSize?: number
  filters?: ReportFilter<RT>
  sortColumn?: ReportTypeMap[RT]['pageColumn']
  sortDirection?: 'asc' | 'desc'
} & (RT extends 'time_log'
  ? {
      groupColumn?: 'user.id' | 'facility.id'
      groupDateInterval?: 'week' | 'month' | 'year'
    }
  : object)

export const getReportPage = async <RT extends keyof ReportTypeMap>(
  reportType: RT,
  options: GetReportPageOptions<RT>,
): Promise<ReportTypeMap[RT]['page']> => {
  // Client uses camelCased column accessor keys, but API expects snake
  const sortColumn = options.sortColumn
    ? toDotSnakeCase(options.sortColumn)
    : undefined

  const filters = options.filters
    ? JSON.stringify(filterToDotSnakeCase(options.filters))
    : undefined

  const groupColumn = 'groupColumn' in options ? options.groupColumn : undefined
  const groupDateInterval =
    'groupDateInterval' in options ? options.groupDateInterval : undefined

  const response = await axiosClient.get<ReportTypeMap[RT]['page']>(
    `/reports/${reportType}s/data`,
    {
      params: {
        ...options,
        groupColumn,
        groupDateInterval,
        sortColumn,
        filters,
      },
    },
  )

  return response.data
}

export const getPipelineSteps = async () => {
  const response = await axiosClient.get<Step[]>('/shotgrid/pipeline_steps')
  return response.data
}

export const getDepartments = async () => {
  const response = await axiosClient.get<Department[]>('/departments')
  return response.data
}

export const getCostCenters = async () => {
  const response = await axiosClient.get<CostCenter[]>('/cost_centers')
  return response.data
}

export const getShotgridEntitySchemas = async () => {
  const response = await axiosClient.get<ShotgridEntitySchema[]>(
    '/shotgrid/entity_schemas',
  )
  return response.data
}

export const getFacilities = async () => {
  const response = await axiosClient.get<Facility[]>('/facilities')
  return response.data
}

export const createCorrectedTimeCard = async ({
  userId,
  date,
}: {
  userId: number
  date: DateTime
}) => {
  return axiosClient.post(`/users/${userId}/corrected_time_cards`, { date })
}

export const deleteCorrectedTimeCard = async ({
  userId,
  timeCardId,
}: {
  userId: number
  timeCardId: number
}) => {
  return axiosClient.delete(
    `/users/${userId}/corrected_time_cards/${timeCardId}`,
  )
}

export const updateCorrectedTimeCard = async (options: {
  userId: number
  id: number
  startAt: number | null
  endAt: number | null
}) => {
  const partialCard = {
    startAt: options.startAt,
    endAt: options.endAt,
  }
  return axiosClient.put(
    `/users/${options.userId}/corrected_time_cards/${options.id}`,
    partialCard,
  )
}

export const saveCorrectedTimeCard = async ({
  userId,
  timeCardId,
}: {
  userId: number
  timeCardId: number
}) => {
  return axiosClient.post(
    `/users/${userId}/corrected_time_cards/${timeCardId}/save `,
  )
}

export const searchDayLocks = async (
  userId: number,
  start: DateTime,
  end: DateTime,
): Promise<DayLock[]> => {
  const response = await axiosClient.get<DayLock[]>(
    `/users/${userId}/day_lock_searches`,
    { params: { startDate: start, endDate: end } },
  )

  return response.data
}

export const searchAdminLocks = async (start: DateTime, end: DateTime) => {
  const response = await axiosClient.get<DayLock[]>('/admin/lock_searches', {
    params: { startDate: start, endDate: end },
  })
  return response.data
}

export interface DayLockPayload {
  workdayWorkerId: number
  date: DateTime
}

export const upsertDayLocks = async (
  days: DayLockPayload[],
  type: DayLockType,
) => {
  return axiosClient.put('/admin/locks', {
    days,
    type,
  })
}

export const deleteDayLocks = async (days: DayLockPayload[]) => {
  return axiosClient.delete('/admin/locks', {
    data: {
      days,
    },
  })
}

export const sendTimeCardsToPayroll = async (timeCardIds: number[]) => {
  const response = await axiosClient.post<BatchOperationResponse>(
    '/admin/time_cards/send_to_payroll',
    {
      timeCardIds,
    },
  )

  return response.data
}

export const unsendTimeCardsFromPayroll = async (timeCardIds: number[]) => {
  const response = await axiosClient.post<BatchOperationResponse>(
    '/admin/time_cards/unsend_from_payroll',
    {
      timeCardIds,
    },
  )

  return response.data
}

export const getSavedReports = async () => {
  const { data } = await axiosClient.get<
    SavedReportWithSerializedDateTimes<keyof ReportTypeMap>[]
  >('/saved_reports')

  return data.map(savedReportFilterDeserializer)
}

export const getSavedReport = async (id: number) => {
  const { data } = await axiosClient.get<
    SavedReportWithSerializedDateTimes<keyof ReportTypeMap>
  >(`/saved_reports/${id}`)

  return savedReportFilterDeserializer(data)
}

export const createSavedReport = async <RT extends keyof ReportTypeMap>({
  name,
  description,
  columns,
  filters,
  sortColumn,
  sortDirection,
  isPublic,
  type,
  groupColumn,
  groupDateInterval,
}: Pick<
  SavedReport<RT>,
  | 'name'
  | 'description'
  | 'columns'
  | 'filters'
  | 'sortColumn'
  | 'sortDirection'
  | 'isPublic'
  | 'type'
  | 'groupColumn'
  | 'groupDateInterval'
>) => {
  const { data, ...response } = await axiosClient.post<
    SavedReportWithSerializedDateTimes<keyof ReportTypeMap>
  >('/saved_reports', {
    name,
    description,
    columns,
    filters,
    sortColumn,
    sortDirection,
    isPublic,
    type,
    groupColumn,
    groupDateInterval,
  })

  return { ...response, data: savedReportFilterDeserializer(data) }
}

export const updateSavedReport = async <RT extends keyof ReportTypeMap>(
  options: Partial<
    Pick<
      SavedReport<RT>,
      | 'name'
      | 'description'
      | 'columns'
      | 'filters'
      | 'sortColumn'
      | 'sortDirection'
      | 'isPublic'
      | 'groupColumn'
      | 'groupDateInterval'
      | 'apiAccessEnabled'
      | 'apiColumns'
      | 'apiFilters'
      | 'apiSortColumn'
      | 'apiGroupColumn'
    >
  > & { id: number },
) => {
  const { data, ...response } = await axiosClient.put<
    SavedReportWithSerializedDateTimes<RT>
  >(`/saved_reports/${options.id}`, options)

  return { ...response, data: savedReportFilterDeserializer(data) }
}

export const deleteSavedReport = async (id: number) => {
  return await axiosClient.delete(`/saved_reports/${id}`)
}

interface FacilityTimeCardStatusTotals {
  facilityId: number
  timeCardStatusTotals: StatusMap<number>
  timeCardIssues: {
    hasTimeLoggedToUnlinkedProject: number
    hasTimeLoggedToInactiveProject: number
    hasDifferentDayNumberThanWasSent: number
    workdaySendError: number
  }
}

export const getFacilityTimeCardStatusTotals = async (
  start: DateTime,
  end: DateTime,
): Promise<FacilityTimeCardStatusTotals[]> => {
  const response = await axiosClient.get<FacilityTimeCardStatusTotals[]>(
    '/admin/facility_time_card_status_totals',
    {
      params: { startDate: start, endDate: end },
    },
  )

  return response.data
}

export const searchPayPeriods = async (
  start: DateTime,
  end: DateTime,
): Promise<PayPeriod[]> => {
  const response = await axiosClient.get<PayPeriod[]>('/pay_period_searches', {
    params: { startDate: start, endDate: end },
  })

  return response.data
}

export const getProjectIntegrations = async (): Promise<
  ProjectIntegration[]
> => {
  const response = await axiosClient.get<ProjectIntegration[]>(
    '/project_integrations',
  )
  return response.data
}

export const createIntegrationProjectLink = async (
  projectId: number,
  projectIntegrationId: number,
  sourceProjectId: number,
): Promise<AdminProject> => {
  const response = await axiosClient.post<AdminProject>(
    `/admin/projects/${projectId}/integration_links`,
    { projectIntegrationId, sourceProjectId },
  )
  return response.data
}

export const deleteIntegrationProjectLink = async (
  projectId: number,
  integrationProjectLinkId: number,
): Promise<AdminProject> => {
  const response = await axiosClient.delete<AdminProject>(
    `/admin/projects/${projectId}/integration_links/${integrationProjectLinkId}`,
  )
  return response.data
}

export const getSourceProjects = async (
  projectIntegrationId: number,
): Promise<SourceProject[]> => {
  const response = await axiosClient.get<SourceProject[]>(
    `/project_integrations/${projectIntegrationId}/source_projects`,
  )
  return response.data
}

export const createWorkdayProjectLink = async (
  projectId: number,
  workdayProjectId: string,
): Promise<AdminProject> => {
  const response = await axiosClient.post<AdminProject>(
    `/admin/projects/${projectId}/workday_link`,
    { workdayProjectId },
  )
  return response.data
}

export const deleteWorkdayProjectLink = async (
  projectId: number,
): Promise<AdminProject> => {
  const response = await axiosClient.delete<AdminProject>(
    `/admin/projects/${projectId}/workday_link`,
  )
  return response.data
}

export const getWorkdayProjects = async (): Promise<WorkdayProject[]> => {
  const response = await axiosClient.get<WorkdayProject[]>('/workday/projects')
  return response.data
}

export const getAdminManagedGroups = async (): Promise<AdminManagedGroup[]> => {
  const response = await axiosClient.get<AdminManagedGroup[]>(
    '/admin/managed_groups',
  )
  return response.data
}

export const getAdminManagedGroup = async (id: number) => {
  const response = await axiosClient.get<AdminManagedGroup>(
    `/admin/managed_groups/${id}`,
  )
  return response.data
}

export const createAdminManagedGroup = async (
  adminManagedGroup: Omit<
    AdminManagedGroup,
    'id' | 'creator' | 'updatedAt' | 'createdAt'
  >,
) => {
  const response = await axiosClient.post<AdminManagedGroup>(
    '/admin/managed_groups',
    {
      adminManagedGroup,
    },
  )

  return response.data
}

export const updateAdminManagedGroup = async (
  id: number,
  adminManagedGroup: Omit<
    AdminManagedGroup,
    'creator' | 'updatedAt' | 'createdAt'
  >,
) => {
  const response = await axiosClient.put<AdminManagedGroup>(
    `/admin/managed_groups/${id}`,
    { adminManagedGroup },
  )

  return response.data
}

// export const deleteAdminManagedGroup = (id: number) => {
//   // TODO - update path
//   /*
//   return axiosClient.delete(`/admin/managed_groups/${id}`)
//   */

//   return Promise.resolve()
// }

export const getLatestTaskEstimate = async (taskId: number) => {
  const response = await axiosClient.get<LatestTaskEstimate>(
    `/tasks/${taskId}/estimates/latest`,
  )

  return response.data
}

export const getTaskEstimates = async (taskId: number) => {
  const response = await axiosClient.get<TaskEstimate[]>(
    `/tasks/${taskId}/estimates`,
  )

  return response.data
}

export const saveEstimate = async (
  taskId: number,
  {
    suppliedValue,
    suppliedValueType,
  }: {
    suppliedValue: number
    suppliedValueType: TaskEstimateSuppliedValueType
  },
) => {
  return axiosClient.post(`/tasks/${taskId}/estimates`, {
    taskEstimate: { suppliedValue, suppliedValueType },
  })
}

// API Key Management
export const getUserApiKeys = async (userId: number) => {
  const response = await axiosClient.get<ApiKey[]>(`/users/${userId}/api_keys`)
  return response.data
}

export const createUserApiKey = async (userId: number) => {
  const response = await axiosClient.post<NewApiKey>(
    `/users/${userId}/api_keys`,
  )
  return response.data
}

export const deleteUserApiKey = async (userId: number, apiKeyId: number) => {
  return axiosClient.delete(`/users/${userId}/api_keys/${apiKeyId}`)
}

export const enableUserApiKey = async (userId: number, apiKeyId: number) => {
  const response = await axiosClient.put<ApiKey>(
    `/users/${userId}/api_keys/${apiKeyId}/enable`,
  )
  return response.data
}

export const disableUserApiKey = async (userId: number, apiKeyId: number) => {
  const response = await axiosClient.put<ApiKey>(
    `/users/${userId}/api_keys/${apiKeyId}/disable`,
  )

  return response.data
}

export const getTimeBlocks = async (timeCardId: number) => {
  const response = await axiosClient.get<TimeBlock[]>(
    `/time_cards/${timeCardId}/time_blocks`,
  )

  return response.data
}

export const getBatchOperation = async (batchOperationId: number) => {
  const response = await axiosClient.get<BatchOperation>(
    `/batch_operations/${batchOperationId}`,
  )

  return response.data
}

export const getCorrections = async (
  page: number,
  status?: CorrectionActionFilter,
): Promise<Paginated<TimeCardCorrection>> => {
  const response = await axiosClient.get<Paginated<TimeCardCorrection>>(
    '/admin/corrected_time_cards',
    {
      params: {
        page,
        payrollAction:
          status === CorrectionActionFilter.Pending ? undefined : status,
      },
    },
  )

  return response.data
}

export const takeCorrectionAction = async (
  correctionId: number,
  payrollAction: CorrectionAction,
) => {
  return await axiosClient.post(
    `/admin/corrected_time_cards/${correctionId}/payroll_action`,
    { payrollAction },
  )
}

export const clearCorrectionAction = async (correctionId: number) => {
  return await axiosClient.delete(
    `/admin/corrected_time_cards/${correctionId}/payroll_action`,
  )
}

export const updateTimeCardEntryOrder = async (
  timeCardId: number,
  entryIds: number[],
) => {
  return axiosClient.put(`/admin/time_cards/${timeCardId}/entry_order`, {
    entryIds,
  })
}
